import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Box,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import Tooltip from "@material-ui/core/Tooltip";

import { downloadHistoricalFile } from "../../viewServices/apiCalls";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const sectionFileSelectionStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  sectionFileSelection: {
    display: "flex",
    alignItems: "stretch",
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    justifyContent: "flex-start",
    gap: theme.spacing(4),
  },
  sectionFileSelectionReadonly: {
    justifyContent: "flex-start",
    gap: theme.spacing(2),
  },
  fileDropZone: {
    border: "2px dashed",
    borderColor: grayColor[4],
    borderRadius: "8px",
    width: 220,
    textAlign: "center",
    padding: theme.spacing(2),
    cursor: "pointer",
    position: "relative",
    transition: "box-shadow 0.3s, border-color 0.3s",
    backgroundColor: grayColor[12],
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 80,
    flex: 1,
    "&:hover": {
      borderColor: clientColor,
      backgroundColor: grayColor[11],
    },
  },
  fileDropHover: {
    borderColor: clientColor,
    backgroundColor: clientColor,
    color: whiteColor,
  },
  fileDropDefault: {},
  hiddenInput: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    cursor: "pointer",
  },
  smallText: {
    fontSize: "0.875rem",
    color: grayColor[1],
  },
  button: {
    backgroundColor: clientColor,
    color: whiteColor,
    "&:hover": {
      backgroundColor: clientColor,
    },
  },
  inputLabel: {
    fontWeight: 500,
    fontSize: "0.875rem",
    color: grayColor[2],
    marginBottom: theme.spacing(0.5),
  },
  select: {
    height: 40,
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    minWidth: 80,
  },
  headerRowContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: 150,
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignSelf: "stretch",
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 200,
    alignSelf: "stretch",
  },
});

export default function SectionFileSelection({
  readonly,
  newFile,
  currentFile,
  isPreparingFile,
  fileChecked,
  headerRow,
  implementationColor,
  onFileSelect,
  onFileClear,
  onPrepareFile,
  onHeaderRowChange,
}) {
  const useStyles = makeStyles((theme) =>
    sectionFileSelectionStyle(theme, implementationColor),
  );
  const classes = useStyles();
  const [isDragging, setIsDragging] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const T_PREFIX = "rgm.inputs.file_selection";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const buttonText = isPreparingFile
    ? t("reading")
    : fileChecked
      ? t("reading_done")
      : t("read_headers");

  const isPrepareDisabled = isPreparingFile || fileChecked || !newFile;

  // Add download handler
  const handleDownload = async () => {
    if (!currentFile?.file_name) return;

    setIsDownloading(true);
    try {
      const fileName = `${currentFile.file_name}.${currentFile.file_type}`;
      await downloadHistoricalFile(currentFile.table_id, fileName);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      className={`${classes.sectionFileSelection} ${
        readonly ? classes.sectionFileSelectionReadonly : ""
      }`}
    >
      {readonly ? (
        <Box>
          <Typography
            className={classes.smallText}
            style={{ marginBottom: "8px" }}
          >
            <i>{t("current_file")}</i>:
          </Typography>
          {currentFile?.file_name ? (
            <Box display="flex" alignItems="center" gap={1}>
              <span className={classes.smallText}>
                {currentFile.file_name}.{currentFile.file_type}
              </span>
              <Tooltip title={t("download_original_file")}>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <GetAppIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <span className={classes.smallText}>{t("none")}</span>
          )}
        </Box>
      ) : (
        <>
          {/* Left Column - File Drag Zone */}
          <Box className={classes.leftColumn}>
            <Box
              className={`${classes.fileDropZone} ${
                isDragging ? classes.fileDropHover : classes.fileDropDefault
              }`}
              onDragEnter={() => setIsDragging(true)}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file) onFileSelect({ target: { files: [file] } });
              }}
              onDragOver={(e) => e.preventDefault()}
            >
              <Typography
                variant="body2"
                style={{ fontWeight: 500, marginBottom: "4px" }}
              >
                {t("drag_and_drop")}
              </Typography>
              <input
                key={newFile ? newFile.name : "no-file"}
                type="file"
                className={classes.hiddenInput}
                onChange={onFileSelect}
              />
            </Box>
          </Box>

          {/* Right Column - New file info and controls */}
          <Box className={classes.rightColumn}>
            {/* New file to be uploaded - Inline */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" className={classes.smallText}>
                <i>{t("new_file")}</i>:
              </Typography>
              <Typography variant="body2" className={classes.smallText}>
                {newFile ? newFile.name : t("none")}
              </Typography>
              {newFile && (
                <IconButton size="small" onClick={onFileClear}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Header Row Selector and Prepare Button - Inline */}
            <Box className={classes.headerRowContainer}>
              <Typography className={classes.inputLabel}>
                {t("header_row")}
              </Typography>
              <FormControl
                className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
              >
                <Select
                  value={headerRow}
                  onChange={(e) => onHeaderRowChange(e.target.value)}
                  disabled={!newFile || fileChecked}
                  MenuProps={{
                    className: classes.selectMenu,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  classes={{
                    select: `${classes.select} ${classes.selectFormControlOverride}`,
                  }}
                >
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((rowNum) => (
                    <MenuItem
                      key={rowNum}
                      value={rowNum}
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {rowNum}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Prepare Button */}
              <Button
                onClick={onPrepareFile}
                variant="contained"
                disabled={isPrepareDisabled}
                className={classes.button}
              >
                {buttonText}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

SectionFileSelection.propTypes = {
  readonly: PropTypes.bool.isRequired,
  newFile: PropTypes.any,
  currentFile: PropTypes.any,
  isPreparingFile: PropTypes.bool.isRequired,
  fileChecked: PropTypes.bool.isRequired,
  headerRow: PropTypes.number.isRequired,
  currentHeaderRow: PropTypes.number,
  implementationColor: PropTypes.string.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  onFileClear: PropTypes.func.isRequired,
  onPrepareFile: PropTypes.func.isRequired,
  onHeaderRowChange: PropTypes.func.isRequired,
};
